import { isNotEmpty } from '@k2/common/helpers';
import { ApiClient } from '@k2/common/backend/api-client';
import {
  Partner, PartnerAccreditation,
  PartnerBrief,
  PartnerContact,
  PartnerDetails, PartnerDocument,
  PartnerFeedback, PartnerPrice, PartnerRegions,
  PartnerReview, PartnerSustainability,
  PartnerTeam
} from '@k2/common/entities-state/types';
import { RemoteMutatedFile } from '@k2/common/file-jet/file';
import { toPairs } from 'ramda';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export class PartnersEndpoint {
  constructor(private api: ApiClient) {
  }

  fetchPartners = () => {
    return this.api.get(`/staff/vendors`);
  };

  fetchNewPartnerForm = () => {
    return this.api.get(`/staff/vendors/new`).pipe(map(payload => payload.form));
  };

  createPartner = (data: PartnerDetails): Observable<number> => {
    return this.api.post(`/staff/vendors`, data).pipe(map(payload => payload.id));
  };

  fetchPartnerForm = (id: number) => {
    return this.api.get(`/staff/vendors/${id}`).pipe(map(payload => payload.form));
  };

  updatePartner = (id: number, data: PartnerDetails) => {
    return this.api.patch(`/staff/vendors/${id}`, data);
  };

  deletePartner = (id: number) => {
    return this.api.delete(`/staff/vendors/${id}`);
  };

  changeStatusPartner = (id: number, status: string) => {
    return this.api.patch(`/staff/vendors/status/${status}/${id}`, {});
  };

  fetchPartner = (id: number) => {
    return this.api.get(`/staff/vendors/${id}/overview`);
  };

  fetchPartnerRecentInitiations = (id: number) => {
    return this.api.get(`/staff/vendors/${id}/recently-initiated-pce-sent`);
  }

  fetchTeam = (partnerId: number) => {
    return this.api.get(`/staff/vendors/${partnerId}/team`).pipe(map(payload => payload.team));
  };

  fetchTeamForm = (partnerId: number) => {
    return this.api.get(`/staff/vendors/${partnerId}/team/edit`).pipe(map(payload => payload.form));
  };

  updateTeam = (partnerId: number, data: PartnerTeam) => {
    return this.api.patch(`/staff/vendors/${partnerId}/team/edit`, data);
  };

  fetchBriefForm = (partnerId: number) => {
    return this.api.get(`/staff/vendors/${partnerId}/brief`).pipe(map(payload => payload.form));
  };

  updateBrief = (partnerId: number, data: PartnerBrief) => {
    return this.api.patch(`/staff/vendors/${partnerId}/brief`, data);
  };

  fetchFeedback = (partnerId: number): Observable<PartnerFeedback> => {
    return (
      this.api
        // todo: implement offset & limit
        // /staff/vendors/{vendorId}/feedback[/offset/{offset}/limit/{limit}]
        .get(`/staff/vendors/${partnerId}/feedback`)
        .pipe(map(payload => payload.feedback))
    );
  };

  updatePartnerNotes = (partnerId: number, notes: string) => {
    const data = { notes };
    return this.api.patch(`/staff/vendors/${partnerId}/notes`, data);
  };

  updatePartnerLogo = (partnerId: number, logo: RemoteMutatedFile) => {
    return this.api.patch(`/staff/vendors/${partnerId}/avatar`, logo);
  };

  getPartnerLogoPath = (partnerId: number) => {
    return this.api.toUrl(`/vendor-staff/avatar/${partnerId}`);
  };

  deletePartnerLogo = (partnerId: number) => {
    return this.api.delete(`/staff/vendors/${partnerId}/avatar`);
  };

  fetchReviews = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/reviews`)
      .pipe(map(payload => payload.reviews));
  };

  fetchNewReviewForm = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/reviews/new`)
      .pipe(map(payload => payload.form));
  };

  createReview = (partnerId: number, data: PartnerReview) => {
    return this.api.post(`/staff/vendors/${partnerId}/reviews`, data);
  };

  fetchReviewForm = (partnerId: number, reviewId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/reviews/${reviewId}`)
      .pipe(map(payload => payload.form));
  };

  fetchActionItems = (partnerId: number, reviewId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/reviews/${reviewId}/action-items`)
      .pipe(map(payload => payload.action_items));
  };

  deleteActionItem = (partnerId: number, reviewId: number, actionItemId: number) => {
    return this.api.delete(`/staff/vendors/${partnerId}/reviews/${reviewId}/action-items/${actionItemId}`);
  };

  fetchNewActionItemForm = (partnerId: number, reviewId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/reviews/${reviewId}/action-items/new`)
      .pipe(map(payload => payload.form));
  };

  // TODO: replace any with PartnerActionItem in phase 2
  updateActionItem = (partnerId: number, reviewId: number, actionId: number, data: any) => {
    return this.api.patch(`/staff/vendors/${partnerId}/reviews/${reviewId}/action-item/${actionId}`, data);
  };

  // TODO: replace any with PartnerActionItem in phase 2
  createActionItem = (partnerId: number, reviewId: number, data: any) => {
    return this.api.post(`/staff/vendors/${partnerId}/reviews/${reviewId}/action-items`, data);
  };

  fetchActionItemForm = (partnerId: number, reviewId: number, actionId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/reviews/${reviewId}/action-items/${actionId}`)
      .pipe(map(payload => payload.form));
  };

  updateReview = (partnerId: number, reviewId: number, data: PartnerReview) => {
    return this.api.patch(`/staff/vendors/${partnerId}/reviews/${reviewId}`, data);
  };

  fetchContacts = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/contacts`);
  };

  fetchNewContactForm = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/contacts/new`)
      .pipe(map(payload => payload.form));
  };

  createContact = (partnerId: number, data: PartnerContact) => {
    return this.api.post(`/staff/vendors/${partnerId}/contacts`, data);
  };

  createK2Contact = (partnerId: number, data: PartnerContact) => {
    return this.api.post(`/staff/vendors/${partnerId}/contactsk2`, data);
  };

  fetchContactForm = (partnerId: number, contactId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/contacts/${contactId}`)
      .pipe(map(payload => payload.form));
  };

  fetchContact = (partnerId: number, contactId: number) => {
    return this.api.get(`/staff/vendors/${partnerId}/contacts/${contactId}`).pipe(
      map(payload => payload.form.fields),
      map(
        (fields): PartnerContact =>
          ({
            id: fields.user_id.value,
            first_name: fields.first_name.value,
            last_name: fields.last_name.value,
            avatar: fields.avatar.value,
            email: fields.email.value,
            job_title: fields.job_title.value,
            responsibility: fields.responsibility.value,
            telephone_work: fields.telephone.value,
            telephone_mobile: fields.mobile.value,
            main_contact: fields.main_contact.value
          } as any)
      )
    );
  };

  updateContact = (partnerId: number, contactId: number, data: PartnerContact) => {
    return this.api.patch(`/staff/vendors/${partnerId}/contacts/${contactId}`, data);
  };

  deleteContact = (partnerId: number, contactId: number) => {
    return this.api.delete(`/staff/vendors/${partnerId}/contacts/${contactId}`);
  };

  updateContactAvatar = (partnerId: number, contactId: number, avatar: RemoteMutatedFile) => {
    return this.api.patch(`/staff/vendors/${partnerId}/contacts/${contactId}/avatar`, avatar);
  };

  deleteContactAvatar = (partnerId: number, contactId: number) => {
    return this.api.delete(`/staff/vendors/${partnerId}/contacts/${contactId}/avatar`);
  };

  fetchPrices = (partnerId: number) => {
    return this.api.get(`/staff/vendors/${partnerId}/prices`).pipe(map(payload => payload.prices));
  };

  fetchNewPriceForm = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/prices/new`)
      .pipe(map(payload => payload.form));
  };

  createPrice = (partnerId: number, data: PartnerPrice) => {
    return this.api.post(`/staff/vendors/${partnerId}/prices`, data);
  };

  fetchPriceForm = (partnerId: number, priceId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/prices/${priceId}`)
      .pipe(map(payload => payload.form));
  };

  updatePrice = (partnerId: number, priceId: number, data: PartnerPrice) => {
    return this.api.patch(`/staff/vendors/${partnerId}/prices/${priceId}`, data);
  };

  deletePrice = (partnerId: number, priceId: number) => {
    return this.api.delete(`/staff/vendors/${partnerId}/prices/${priceId}`);
  };

  fetchDocuments = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/documents`)
      .pipe(map(payload => payload.documents));
  };

  fetchNewDocumentForm = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/documents/new`)
      .pipe(map(payload => payload.form));
  };

  createDocument = (partnerId: number, data: PartnerDocument) => {
    return this.api.post(`/staff/vendors/${partnerId}/documents`, data);
  };

  fetchDocumentForm = (partnerId: number, documentId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/documents/${documentId}`)
      .pipe(map(payload => payload.form));
  };

  updateDocument = (partnerId: number, documentId: number, data: PartnerDocument) => {
    return this.api.patch(`/staff/vendors/${partnerId}/documents/${documentId}`, data);
  };

  deleteDocument = (partnerId: number, documentId: number) => {
    return this.api.delete(`/staff/vendors/${partnerId}/documents/${documentId}`);
  };

  searchChildren = (query: string): Observable<Partner[]> => {
    return this.api
      .get(`/staff/vendors/child/search/${encodeURIComponent(query)}`)
      .pipe(map(payload => payload.results));
  };

  search = (query: SearchQuery): Observable<Partner[]> => {
    const params = toPairs(query as any)
      .filter(([name, value]) => value != null && isNotEmpty(value))
      .reduce((all, current) => [...all, ...current], [])
      .join('/');

    return this.api.get(`/staff/vendors/search/${params}`).pipe(map(payload => payload.results));
  };

  searchMultiServices = (query: SearchQuery): Observable<Partner[]> => {
    const params = toPairs(query as any)
      .filter(([name, value]) => value != null && isNotEmpty(value))
      .reduce((all, current) => [...all, ...current], [])
      .join('/');

    return this.api
      .get(`/staff/vendors/search-multiple-services/${params}`)
      .pipe(map(payload => payload.results));
  };

  fetchStorageReport = (partnerId: number) => {
    return this.api.get(`/staff/vendors/${partnerId}/storage`);
  };

  fetchRegions(partnerId: number) {
    return this.api.get(`/staff/vendors/${partnerId}/regions`);
  }

  updateRegions(partnerId: number, data: PartnerRegions) {
    return this.api.patch(`/staff/vendors/${partnerId}/regions`, data);
  }

  fetchServices(partnerId: number) {
    return this.api.get(`/staff/vendors/${partnerId}/services`);
  }

  updateServices(partnerId: number, data: number[]) {
    return this.api.patch(`/staff/vendors/${partnerId}/services`, data);
  }

  fetchChildren(partnerId: number) {
    return this.api.get(`/staff/vendors/${partnerId}/children`);
  }

  fetchNewChildForm = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/children/new`)
      .pipe(map(payload => payload.form));
  };

  linkChild = (partnerId: number, data: Partner) => {
    return this.api.post(`/staff/vendors/${partnerId}/children/link`, data);
  };

  unlinkChild = (partnerId: number, childId: number) => {
    return this.api.post(`/staff/vendors/${partnerId}/children/${childId}/unlink`, {});
  };

  fetchSustainability = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/sustainability`);
  };

  deleteAccreditation = (partnerId: number, accreditationId: number) => {
    return this.api.delete(`/staff/vendors/${partnerId}/accreditations/${accreditationId}`);
  };

  fetchNewAccreditationForm = (partnerId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/accreditations/new`)
      .pipe(map(payload => payload.form));
  };

  createAccreditation = (partnerId: number, data: PartnerAccreditation) => {
    return this.api.post(`/staff/vendors/${partnerId}/accreditations`, data);
  };

  fetchAccreditationForm = (partnerId: number, accreditationId: number) => {
    return this.api
      .get(`/staff/vendors/${partnerId}/accreditations/${accreditationId}`)
      .pipe(map(payload => payload.form));
  };

  updateAccreditation = (partnerId: number, accreditationId: number, data: PartnerAccreditation) => {
    return this.api.patch(`/staff/vendors/${partnerId}/accreditations/${accreditationId}`, data);
  };

  updateSustainability(partnerId: number, data: PartnerSustainability) {
    return this.api.patch(`/staff/vendors/${partnerId}/sustainability`, data);
  }
}

interface SearchQuery {
  readonly service?: string;
  readonly regionCode?: string;
  readonly stateCode?: string;
  readonly name?: string;
}
